import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  KpiOutlierMode,
  KpiOutliersResponse,
  KpiSegmentMonthlyResponse,
  PlanningBasisResponse,
  SegmentTargetsPayload,
  ZielrundeResponse,
} from "@msc/shared";

import { api } from "../lib/api.js";

/** Planungsbasis (Arbeitstage + Saisonprofil) für den Verteil-Helfer. */
export function usePlanningBasis(segmentId: string | null, year: number) {
  return useQuery<PlanningBasisResponse>({
    queryKey: ["kpi", "planning-basis", segmentId, year],
    queryFn: () =>
      api.get<PlanningBasisResponse>(
        `/kpi/segments/${segmentId}/planning-basis?year=${year}`,
      ),
    enabled: segmentId !== null,
  });
}

export function useSegmentTargets(segmentId: string | null, year: number) {
  return useQuery<SegmentTargetsPayload>({
    queryKey: ["kpi", "targets", segmentId, year],
    queryFn: () =>
      api.get<SegmentTargetsPayload>(
        `/kpi/segments/${segmentId}/targets/${year}`,
      ),
    enabled: segmentId !== null,
  });
}

export function useSaveSegmentTargets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      year,
      payload,
    }: {
      segmentId: string;
      year: number;
      payload: SegmentTargetsPayload;
    }) =>
      api.put<void>(`/kpi/segments/${segmentId}/targets/${year}`, payload),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({
        queryKey: ["kpi", "targets", vars.segmentId, vars.year],
      });
    },
  });
}

/**
 * Zustand der Zielrunde (offen/eingefroren) samt Pflegestand je Service Lead
 * — Grundlage des Kopfbereichs im Ziele-Reiter (`ZielrundeKopf`).
 */
export function useZielrunde(segmentId: string | null, year: number) {
  return useQuery<ZielrundeResponse>({
    queryKey: ["kpi", "zielrunde", segmentId, year],
    queryFn: () =>
      api.get<ZielrundeResponse>(
        `/kpi/segments/${segmentId}/zielrunde/${year}`,
      ),
    enabled: segmentId !== null,
  });
}

/** „Meine Ziele sind fertig gepflegt" — eigene Freigabe setzen oder zurückziehen. */
export function useZielrundeFreigabe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      year,
      freigegeben,
    }: {
      segmentId: string;
      year: number;
      freigegeben: boolean;
    }) =>
      api.put<void>(`/kpi/segments/${segmentId}/zielrunde/${year}/freigabe`, {
        freigegeben,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** Zielrunde einfrieren — Umsatz auf allen Ebenen festhalten (Segment-Führung). */
export function useZielrundeEinfrieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, year }: { segmentId: string; year: number }) =>
      api.post<void>(
        `/kpi/segments/${segmentId}/zielrunde/${year}/einfrieren`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** Bereichs-KPIs je Monat. */
export function useSegmentMonthlyKpis(segmentId: string | null, year: number) {
  return useQuery<KpiSegmentMonthlyResponse>({
    queryKey: ["kpi", "segment-monthly", segmentId, year],
    queryFn: () =>
      api.get<KpiSegmentMonthlyResponse>(
        `/kpi/segments/${segmentId}/monthly?year=${year}`,
      ),
    enabled: segmentId !== null,
  });
}

export interface TeamMember {
  employeeId: string;
  fullName: string;
  email: string;
  active: boolean;
  careerLevel: string | null;
  federalState: string;
  hoursPerDay: number;
  mentorEmployeeId: string | null;
  yppDate: string | null;
  /** Eintritt; null = „seit jeher" (Backfill ohne bekannte Historie). */
  memberSince: string | null;
  /** Terminierter Austritt (auch zukünftig); null = offen. */
  memberUntil: string | null;
}

export interface FormerTeamMember {
  employeeId: string;
  fullName: string;
  memberSince: string | null;
  memberUntil: string;
}

export interface SegmentTeamResponse {
  members: TeamMember[];
  former: FormerTeamMember[];
}

/** Team des Bereichs aus den Mitgliedschaften (aktuell + Historie). */
export function useSegmentTeam(segmentId: string | null) {
  return useQuery<SegmentTeamResponse>({
    queryKey: ["kpi", "team", segmentId],
    queryFn: () =>
      api.get<SegmentTeamResponse>(`/kpi/segments/${segmentId}/team`),
    enabled: segmentId !== null,
  });
}

/**
 * Austritt setzen, korrigieren (Mitgliedschaft mit terminiertem Austritt
 * bleibt „aktuell") oder mit `validTo: null` wieder aufheben.
 */
export function useEndTeamMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      employeeId,
      validTo,
    }: {
      segmentId: string;
      employeeId: string;
      validTo: string | null;
    }) =>
      api.post<void>(`/kpi/segments/${segmentId}/team/${employeeId}/end`, {
        validTo,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** Kapazität im Detail: pro Mitglied × Monat (erklärt die Soll-Linie). */
export interface CapacityDetailMember {
  employeeId: string;
  fullName: string;
  active: boolean;
  /** Kapazität je Monat — die Soll-Seite der Zelle. */
  monthly: number[];
  total: number;
  /** Faktura-Stunden auf Projekten dieses Bereichs. */
  billed: number[];
  /** Faktura-Stunden auf Projekten anderer Kundensegmente. */
  billedElsewhere: number[];
  /** Faktura-Stunden ausserhalb des Servicegeschäfts (Projektumsetzung u. a.). */
  billedNonService: number[];
  /**
   * Taucht die Person überhaupt je als Berater in einer Projektbesetzung auf?
   * Ist das false, zeigt die Tabelle „–" statt einer harten 0 — die läse sich
   * wie Nichtstun, obwohl es eine Namenslücke ist.
   */
  imSpiegelBekannt: boolean;
}

export interface CapacityDetailResponse {
  year: number;
  overrides: Array<number | null>;
  totals: number[];
  members: CapacityDetailMember[];
}

export function useCapacityDetail(
  segmentId: string | null,
  year: number,
  enabled: boolean,
) {
  return useQuery<CapacityDetailResponse>({
    queryKey: ["kpi", "capacity-detail", segmentId, year],
    queryFn: () =>
      api.get<CapacityDetailResponse>(
        `/kpi/segments/${segmentId}/capacity?year=${year}`,
      ),
    enabled: segmentId !== null && enabled,
  });
}

/** Mitgliedschaft ersatzlos löschen — Fehleingabe, keine Historie. */
export function useDiscardTeamMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      employeeId,
    }: {
      segmentId: string;
      employeeId: string;
    }) => api.delete<void>(`/kpi/segments/${segmentId}/team/${employeeId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** Eintrittsdatum der aktuellen Mitgliedschaft korrigieren (Team-Tab). */
export function useRedateTeamMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      employeeId,
      validFrom,
    }: {
      segmentId: string;
      employeeId: string;
      validFrom: string;
    }) =>
      api.post<void>(`/kpi/segments/${segmentId}/team/${employeeId}/since`, {
        validFrom,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** ------------------------------------------------------------ Leads -- */

export interface LeadOverviewRow {
  employeeId: string;
  fullName: string;
  prevYearRevenueEur: number;
  targetRevenueEur: number | null;
  actualYtdEur: number;
  marginCumPct: number | null;
  forecastEur: number | null;
  forecastGrowthPct: number | null;
}

export function useLeadsOverview(segmentId: string | null, year: number) {
  return useQuery<LeadOverviewRow[]>({
    queryKey: ["kpi", "leads-overview", segmentId, year],
    queryFn: () =>
      api.get<LeadOverviewRow[]>(
        `/kpi/segments/${segmentId}/leads/overview?year=${year}`,
      ),
    enabled: segmentId !== null,
  });
}

export interface LeadMonthlyKpis {
  year: number;
  leadEmployeeId: string;
  annualTargetEur: number | null;
  budgetStartEur: number;
  /** Arbeitstag (YYYY-MM-DD), an dem das Budget im aktuellen Tempo aufgebraucht ist. */
  budgetLeerAm: string | null;
  budgetLeftLiveEur: number;
  months: Array<{
    yearMonth: string;
    target: number | null;
    actual: number;
    prevYear: number;
    growthCumPct: number | null;
    marginPct: number | null;
    budgetRemainingEur: number | null;
    budgetSollLaufzeitEur: number;
    budgetSollZielEur: number | null;
    budgetPrognoseEur: number | null;
  }>;
}

export function useLeadMonthlyKpis(
  segmentId: string | null,
  leadEmployeeId: string | null,
  year: number,
  beauftragungId: string | null = null,
) {
  return useQuery<LeadMonthlyKpis>({
    queryKey: ["kpi", "lead-monthly", segmentId, leadEmployeeId, year, beauftragungId],
    queryFn: () =>
      api.get<LeadMonthlyKpis>(
        `/kpi/segments/${segmentId}/leads/${leadEmployeeId}/monthly?year=${year}${beauftragungId ? `&beauftragungId=${beauftragungId}` : ""}`,
      ),
    enabled: segmentId !== null && leadEmployeeId !== null,
  });
}

export interface LeadEngagementRow {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  deputyEmployeeId: string | null;
  deputyEmployeeName: string | null;
  /**
   * Service Lead kommt aus der Jira-Kundenabteilung (F4) und ist im CRM nicht
   * änderbar. Ohne dieses Feld bot das Lead-Cockpit „Übertragen" auch für
   * solche Beauftragungen an — die API lehnte es mit 409 ab, aber erst
   * nachdem man jemanden ausgewählt hatte (2026-08-12).
   */
  jiraManaged: boolean;
  implementers: Array<{ employeeId: string; fullName: string }>;
  prevYearRevenueEur: number;
  revenueTargetEur: number | null;
  growthTargetPct: number | null;
  /**
   * Beim Einfrieren der Zielrunde festgehaltener Umsatz — „Plan neben Ist"
   * (Zielrunde-Spec). Nur gezeigt, wenn er vom aktuellen Ziel abweicht.
   */
  planRevenueTargetEur: number | null;
  /** Umsatz des laufenden Jahres bis zum letzten gebuchten Monat. */
  actualYtdEur: number;
  /** Deckungsbeitrag kumuliert, in Prozent des Umsatzes; null ohne Umsatz. */
  marginCumPct: number | null;
  /** Begründung der letzten Änderung NACH dem Einfrieren. */
  planChangeReason: string | null;
}

export function useLeadEngagements(
  segmentId: string | null,
  leadEmployeeId: string | null,
  year: number,
) {
  return useQuery<LeadEngagementRow[]>({
    queryKey: ["kpi", "lead-engagements", segmentId, leadEmployeeId, year],
    queryFn: () =>
      api.get<LeadEngagementRow[]>(
        `/kpi/segments/${segmentId}/leads/${leadEmployeeId}/engagements?year=${year}`,
      ),
    enabled: segmentId !== null && leadEmployeeId !== null,
  });
}

/** Segmente, in denen der eingeloggte Nutzer Lead ist („Mein Portfolio"). */
export interface MyLeadSegment {
  segmentId: string;
  name: string;
  engagementCount: number;
}

export function useMyLeadSegments(enabled = true) {
  return useQuery<MyLeadSegment[]>({
    queryKey: ["kpi", "my-lead-segments"],
    queryFn: () => api.get<MyLeadSegment[]>("/kpi/my-lead-segments"),
    enabled,
  });
}

/** Drill-down: KPIs einer einzelnen Beauftragung (wie Lead-Überblick + Monate). */
export interface EngagementKpis {
  engagementId: string;
  engagementName: string;
  customerId: string;
  customerName: string;
  segmentId: string;
  year: number;
  prevYearRevenueEur: number;
  annualTargetEur: number | null;
  actualYtdEur: number;
  marginCumPct: number | null;
  forecastEur: number | null;
  forecastGrowthPct: number | null;
  months: Array<{
    yearMonth: string;
    target: number | null;
    actual: number;
    prevYear: number;
    growthCumPct: number | null;
    marginPct: number | null;
  }>;
}

export function useEngagementKpis(engagementId: string | null, year: number) {
  return useQuery<EngagementKpis>({
    queryKey: ["kpi", "engagement-kpis", engagementId, year],
    queryFn: () =>
      api.get<EngagementKpis>(`/kpi/engagements/${engagementId}/kpis?year=${year}`),
    enabled: engagementId !== null,
  });
}

/** Ausreißer-Matrix (Kunden- oder Mitarbeiter-Umsatz mit Median-Flags). */
export function useKpiOutliers(
  segmentId: string | null,
  mode: KpiOutlierMode,
  months: number,
  threshold: number,
  minDeviation: number,
  /** Nur Kunden, für die der Aufrufer Service Lead ist. */
  nurEigene = false,
) {
  return useQuery<KpiOutliersResponse>({
    queryKey: [
      "kpi",
      "outliers",
      segmentId,
      mode,
      months,
      threshold,
      minDeviation,
      nurEigene,
    ],
    queryFn: () =>
      api.get<KpiOutliersResponse>(
        `/kpi/outliers?segmentId=${segmentId}&mode=${mode}&months=${months}&threshold=${threshold}&minDeviation=${minDeviation}&nurEigene=${nurEigene}`,
      ),
    enabled: segmentId !== null,
    placeholderData: (prev) => prev,
  });
}

/** Mehrere Mitarbeiter auf einmal dem Bereich zuordnen (Team-Tab). */
export function useAddTeamMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      segmentId,
      employeeIds,
      validFrom,
    }: {
      segmentId: string;
      employeeIds: string[];
      /** Eintritt ab (Default serverseitig: heute). */
      validFrom?: string;
    }) =>
      api.post<{ added: number }>(`/kpi/segments/${segmentId}/team`, {
        employeeIds,
        ...(validFrom ? { validFrom } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}
